import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaTimes, FaPlus } from "react-icons/fa";

import {
  getProductRecommendations,
  getCartRecommendations,
  trackUpsellEvent,
} from "../actions/upsellActions";
import { addToCart } from "../actions/cartActions";
import ProductImage from "./ProductImage";
import { t } from "../i18n/translations";
import "./UpsellModule.css";

/* Smart Upsell (Pro-only) — "frequently ordered together" recommendation
   module, shared between the item-detail sheet ("item_detail" context)
   and the cart review screen ("cart" context). Always a dismissible,
   secondary suggestion: renders nothing while loading (no skeleton, no
   layout shift competing with the real ordering flow) and nothing at all
   for non-Pro/disabled restaurants or once dismissed. The backend already
   enforces the Pro gate — the client-side subscription_tier check here is
   purely to skip the network request outright, not a security boundary. */
function UpsellModule({ context, sourceProduct, cartProductIds }) {
  const dispatch = useDispatch();
  const { language } = useSelector((state) => state.language);
  const { restaurant = {} } = useSelector((state) => state.restaurantDetails || {});
  const hidePrices = restaurant.hide_prices === true;
  const isPro = restaurant.subscription_tier === "pro";

  const [dismissed, setDismissed] = useState(false);
  const trackedRef = useRef(new Set());

  const cartKey = (cartProductIds || []).join(",");

  const productRecs = useSelector((state) => state.productRecommendations);
  const cartRecs = useSelector((state) => state.cartRecommendations);
  const { recommendations = [], isPatternBased = false, loading } =
    context === "item_detail" ? productRecs : cartRecs;

  useEffect(() => {
    setDismissed(false);
  }, [context, sourceProduct, cartKey]);

  useEffect(() => {
    if (!isPro) return;

    if (context === "item_detail" && sourceProduct) {
      dispatch(getProductRecommendations(sourceProduct));
    } else if (context === "cart" && cartProductIds?.length) {
      dispatch(getCartRecommendations(cartProductIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPro, context, sourceProduct, cartKey]);

  useEffect(() => {
    if (dismissed || !recommendations.length) return;

    for (const item of recommendations) {
      const trackKey = `${sourceProduct || "cart"}:${item._id}`;
      if (trackedRef.current.has(trackKey)) continue;
      trackedRef.current.add(trackKey);

      dispatch(
        trackUpsellEvent({
          event_type: "impression",
          source_product_id: context === "item_detail" ? sourceProduct : undefined,
          recommended_product_id: item._id,
          context,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations, dismissed]);

  if (!isPro || loading || dismissed || recommendations.length === 0) {
    return null;
  }

  const titleKey =
    context === "item_detail"
      ? isPatternBased
        ? "upsellPatternTitleDetail"
        : "upsellFallbackTitleDetail"
      : isPatternBased
        ? "upsellPatternTitleCart"
        : "upsellFallbackTitleCart";

  const handleAdd = (product) => {
    dispatch(
      addToCart({
        product: product._id,
        name: product.name,
        qty: 1,
        price: Number(product.price),
        image: product.image,
        selectedOptions: [],
        isUpsell: true,
        upsellSourceProduct: context === "item_detail" ? sourceProduct : null,
      }),
    );

    dispatch(
      trackUpsellEvent({
        event_type: "add",
        source_product_id: context === "item_detail" ? sourceProduct : undefined,
        recommended_product_id: product._id,
        context,
      }),
    );
  };

  return (
    <div className="upsell-module">
      <div className="upsell-header">
        <span className="upsell-title">{t(titleKey, language)}</span>
        <button
          type="button"
          className="upsell-dismiss"
          onClick={() => setDismissed(true)}
          aria-label={t("upsellDismissAria", language)}
        >
          <FaTimes />
        </button>
      </div>

      <div className="upsell-cards">
        {recommendations.map((product) => (
          <div key={product._id} className="upsell-card">
            <ProductImage
              src={product.image}
              alt={product.name}
              className="upsell-card-image"
            />
            <div className="upsell-card-info">
              <span className="upsell-card-name">{product.name}</span>
              {!hidePrices && (
                <span className="upsell-card-price">{product.price} Ft</span>
              )}
            </div>
            <button
              type="button"
              className="upsell-card-add"
              onClick={() => handleAdd(product)}
              aria-label={t("addToCart", language)}
            >
              <FaPlus />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default UpsellModule;
